import React, { useEffect, useRef, useState } from 'react';
import { Button, ConfigProvider, Input, Typography, Alert } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import authActions from '../../actions/authActions';
import AnimatedDrivingScene from '../../components/svg/animatedDrivingScene';
import { getDeviceFingerprint } from '../../utils/deviceFingerprint';

const { Title, Text } = Typography;

const OtpVerification: React.FC = () => {
  const [digits, setDigits] = useState<string[]>(['', '', '', '']);
  const inputsRef = useRef<Array<any>>([]);
  const [status, setStatus] = useState<{ type: 'success' | 'error' | 'info' | null; text?: string }>({ type: null });
  const [counter, setCounter] = useState(300);
  const [canResend, setCanResend] = useState(false);
  const authlogin = useSelector((state: any) => state.otpverifyReducer);
  const authErrorMessage = authlogin?.isError
    ? (authlogin?.error?.message || authlogin?.error?.errorMessage || authlogin?.error?.response?.data?.errorMessage || 'Unable to verify your code right now.')
    : null;
  const resendData = useSelector((state: any) => state.resendReducer);
  const countdownRef = useRef<number | null>(null);
  const preferenceState = useSelector((state: any) => state.getPreferenceByPhoneReducer);
  const lastOtpRef = useRef<string>('');
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const resendState = useSelector((state: any) => state.resendReducer);
  const phone = (location.state as any)?.mobileNumber as string | undefined;
  const redirectTo = (location.state as any)?.redirectTo as string | undefined;
  const plan = (location.state as any)?.plan as string | undefined;
  const billing = (location.state as any)?.billing as string | undefined;

  useEffect(() => {
    const activePhone = sessionStorage.getItem('OTP_ACTIVE_MOBILE');
    if (!phone || activePhone !== phone) {
      sessionStorage.removeItem('OTP_ACTIVE_MOBILE');
      navigate('/login', { replace: true });
      return;
    }
  }, [phone, navigate]);

  useEffect(() => {
    if (authlogin?.deviceLimitReached) {
      navigate('/device-restriction', {
        state: {
          mobileNumber: phone,
          otp: lastOtpRef.current || digits.join(''),
          selectionToken: authlogin?.selectionToken,
          deviceLimit: authlogin?.deviceLimit,
          sessions: authlogin?.sessions || [],
          redirectTo,
          plan,
          billing,
        },
      });
    }
  }, [authlogin?.deviceLimitReached, navigate, phone, digits, redirectTo, plan, billing]);

  const startCountdown = () => {
    if (countdownRef.current) {
      window.clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
    setCanResend(false);
    countdownRef.current = window.setInterval(() => {
      setCounter((c) => {
        if (c <= 1) {
          if (countdownRef.current) {
            window.clearInterval(countdownRef.current);
            countdownRef.current = null;
          }
          setCanResend(true);
          return 0;
        }
        return c - 1;
      });
    }, 1000);
  };

  const parseDurationToSeconds = (timeStr: string | undefined, fallback = 300) => {
    if (!timeStr || typeof timeStr !== 'string') return fallback;
    const match = timeStr.match(/^(\d+)([smhd])$/);
    if (!match) return fallback;
    const value = parseInt(match[1], 10);
    const unit = match[2];
    switch (unit) {
      case 's':
        return value;
      case 'm':
        return value * 60;
      case 'h':
        return value * 3600;
      case 'd':
        return value * 86400;
      default:
        return fallback;
    }
  };

  const formatTime = (seconds: number) => {
    const s = Math.max(0, Math.floor(seconds));
    if (s >= 86400) {
      const days = Math.floor(s / 86400);
      let rem = s % 86400;
      const hours = Math.floor(rem / 3600);
      rem = rem % 3600;
      const minutes = Math.floor(rem / 60);
      const secs = rem % 60;
      return `${days}d ${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
    }
    if (s >= 3600) {
      const hours = Math.floor(s / 3600);
      const minutes = Math.floor((s % 3600) / 60);
      const secs = s % 60;
      return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
    }
    const mm = Math.floor(s / 60).toString().padStart(2, '0');
    const ss = (s % 60).toString().padStart(2, '0');
    return `${mm}:${ss}`;
  };

  useEffect(() => {
    const otpSeconds = parseDurationToSeconds(preferenceState?.preferenceData?.otpExpiryTime, 300);
    setCounter(otpSeconds);
    startCountdown();
    return () => {
      if (countdownRef.current) {
        window.clearInterval(countdownRef.current);
        countdownRef.current = null;
      }
    };
  }, [phone, preferenceState?.preferenceData?.otpExpiryTime]);

  const handleChange = (index: number, value: string) => {
    const v = value.replace(/\D/g, '').slice(0, 1);
    const next = [...digits];
    next[index] = v;
    setDigits(next);
    if (v && index < inputsRef.current.length - 1) {
      inputsRef.current[index + 1]?.focus?.();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      if (digits[index]) {
        const next = [...digits];
        next[index] = '';
        setDigits(next);
        return;
      }
      if (index > 0) inputsRef.current[index - 1]?.focus?.();
    }
  };

  const hasSubmittedRef = useRef(false);

  useEffect(() => {
    const allFilled = digits.every((d) => d && d.length === 1);
    if (allFilled && !hasSubmittedRef.current && !authlogin?.isLoading) {
      hasSubmittedRef.current = true;
      handleVerify();
    }
    if (!allFilled) {
      hasSubmittedRef.current = false;
    }
  }, [digits, authlogin?.isLoading]);

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const paste = e.clipboardData.getData('text') || '';
    const onlyDigits = paste.replace(/\D/g, '').slice(0, 4).split('');
    if (onlyDigits.length) {
      const next = ['', '', '', ''];
      for (let i = 0; i < onlyDigits.length; i++) next[i] = onlyDigits[i];
      setDigits(next);
      const lastIndex = Math.min(onlyDigits.length - 1, inputsRef.current.length - 1);
      setTimeout(() => inputsRef.current[lastIndex]?.focus?.(), 50);
      e.preventDefault();
    }
  };

  useEffect(() => {
    if (authlogin && authlogin?.isSuccess && authlogin?.authData?.authToken) {
      sessionStorage.removeItem('OTP_ACTIVE_MOBILE');
      const authData = authlogin.authData;
      localStorage.setItem('AUTHDATA', JSON.stringify(authData));
      if (authData.sessionId) {
        localStorage.setItem('SESSIONID', authData.sessionId);
      }
      window.dispatchEvent(new Event('authStateChanged'));

      const parsed = JSON.parse(localStorage.getItem('AUTHDATA') || '{}');
      let sessionId = parsed?.sessionId;
      localStorage.setItem('SESSIONID', sessionId || '');

      
      setTimeout(() => {
        if (redirectTo === 'pricing' && plan && billing) {
          navigate(`/pricing?plan=${plan}&billing=${billing}`, { replace: true });
        } else {
          navigate('/dashboard', { replace: true });
        }
      }, 100);

      authlogin.isSuccess = false;
    }
  }, [authlogin, navigate, redirectTo, plan, billing]);

  const handleVerify = () => {
    setStatus({ type: null });
    const entered = digits.join('');
    lastOtpRef.current = entered;

    dispatch(authActions.otpVerifyRequest({
      mobileNumber: phone,
      otp: entered,
      isMobile: false,
      sessionId: localStorage.getItem('SESSIONID') || undefined,
      deviceFingerprint: getDeviceFingerprint(),
      userAgent: navigator.userAgent,
    }));
  };

  const handleResend = () => {
    if (!phone) return;
    dispatch(authActions.resendOtpRequest({ mobileNumber: phone, isMobile: false }));
    setDigits(['', '', '', '']);
    inputsRef.current[0]?.focus?.();
  };

  useEffect(() => {
    if (resendData && resendData?.isSuccess) {
      const otpSeconds = parseDurationToSeconds(preferenceState?.preferenceData?.otpExpiryTime, 300);
      setCounter(otpSeconds);
      startCountdown();
    }
  }, [resendData]);

  return (
    <ConfigProvider theme={{ token: { colorPrimary: '#4F86F7', borderRadius: 8, colorTextBase: '#1f2937' } }}>
      <div className="split-layout-wrapper">
        <div className="split-left">
          <AnimatedDrivingScene
            title="Master the Road"
            subtitle="Your journey to becoming a confident driver starts here. Professional instruction, modern tools."
          />
        </div>
        <div className="split-right">
          <div className="auth-bg-decoration">
            <div className="floating-shape shape-1"></div>
            <div className="floating-shape shape-2"></div>
            <div className="floating-shape shape-3"></div>
            <div className="floating-shape shape-4"></div>
          </div>
          <div className="auth-form-container">
            <div className="auth-form-content">
              <Title level={2} className="auth-title verifytitle">Verify your account</Title>
              <Text className="auth-subtitle verifytitlecode">
                Enter the 4-digit code sent to +91 {phone || '••••'}
              </Text>

              <div className="otp-inputs-container">
                {[0, 1, 2, 3].map((i) => (
                  <Input
                    key={i}
                    size="large"
                    value={digits[i]}
                    onChange={(e) => handleChange(i, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(i, e)}
                    onPaste={handlePaste}
                    onFocus={(e) => (e.target as HTMLInputElement).select()}
                    maxLength={1}
                    ref={(el) => { inputsRef.current[i] = el; }}
                    className="otp-input-box"
                  />))}
              </div>
              <div className="otp-submit-section">
                <Button type="primary" size="large" block onClick={handleVerify} loading={authlogin?.isLoading} disabled={digits.some((d) => !d)} className="auth-submit-btn">
                  Verify & Proceed
                </Button>
              </div>
              <div className="otp-resend-section">
                <Text className="resend-text">Didn't receive the code? </Text>
                <Button type="link" onClick={handleResend} disabled={!canResend || resendState?.isLoading}
                  loading={resendState?.isLoading} className="resend-link-btn">
                  {canResend ? 'Resend code' : `Resend in ${formatTime(counter)}`}
                </Button>
              </div>
              <div className="back-to-login-section">
                <Button
                  type="link"
                  onClick={() => {
                    sessionStorage.removeItem('OTP_ACTIVE_MOBILE');
                    dispatch(authActions.clearDeviceLimit());
                    navigate('/login', { replace: true });
                  }}
                  className="back-link-btn"
                >
                  ← Back to login
                </Button>
              </div>

            </div>
          </div>
        </div>
      </div>
    </ConfigProvider>
  );
};

export default OtpVerification;
